import React, { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { putToStorage } from '../../lib/storage';
import { Button, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { useMaxUploadBytes } from '../../app/PropertyContext';
import MentionTextarea from './MentionTextarea';
import './comments.css';

const MAX_FILES = 6;

/**
 * CommentComposer — shared comment box with @mention typeahead and file attachments.
 *
 * Props:
 *   basePath: document API path, e.g. `/requisitions/{id}` (uses `{basePath}/comments` + `/comments/upload-url`)
 *   onPosted: called with the created activity after a successful post
 */
export default function CommentComposer({ basePath, onPosted }) {
  const t = useT();
  const toast = useToast();
  const { maxMb } = useMaxUploadBytes();
  const fileInputRef = useRef(null);

  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [files, setFiles] = useState([]); // { key, attachmentId, storageKey, fileName, contentType, sizeBytes, status }
  // Every mention ever inserted this session; on submit only names still present in the text count.
  const [insertedMentions, setInsertedMentions] = useState([]);

  const pickFiles = () => fileInputRef.current?.click();

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of picked) {
      if (files.length + picked.indexOf(file) >= MAX_FILES) {
        toast.error(t('A comment can carry at most {n} attachments.', { n: MAX_FILES }));
        break;
      }
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(t('"{name}" is larger than {n} MB.', { name: file.name, n: maxMb }));
        continue;
      }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const contentType = file.type || 'application/octet-stream';
      setFiles((prev) => [...prev, {
        key, attachmentId: null, storageKey: null,
        fileName: file.name, contentType, sizeBytes: file.size, status: 'uploading',
      }]);
      try {
        const ticket = await api.post(`${basePath}/comments/upload-url`,
          { fileName: file.name, contentType, sizeBytes: file.size });
        await putToStorage(ticket.uploadUrl, file, contentType);
        setFiles((prev) => prev.map((f) => (f.key === key
          ? { ...f, attachmentId: ticket.attachmentId, storageKey: ticket.storageKey, status: 'ready' }
          : f)));
      } catch (err) {
        setFiles((prev) => prev.filter((f) => f.key !== key));
        toast.error(t('Could not upload "{name}"', { name: file.name }), { description: err?.message });
      }
    }
  };

  const removeFile = (key) => setFiles((prev) => prev.filter((f) => f.key !== key));

  const uploading = files.some((f) => f.status === 'uploading');
  const ready = files.filter((f) => f.status === 'ready');
  const canPost = !posting && !uploading && (body.trim().length > 0 || ready.length > 0);

  const post = async () => {
    if (!canPost) return;
    const text = body.trim();
    // Only mentions whose "@Full Name" survived editing are sent.
    const mentions = insertedMentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);
    setPosting(true);
    try {
      const activity = await api.post(`${basePath}/comments`, {
        body: text,
        mentions,
        attachments: ready.map((f) => ({
          attachmentId: f.attachmentId,
          storageKey: f.storageKey,
          fileName: f.fileName,
          contentType: f.contentType,
          sizeBytes: f.sizeBytes,
        })),
      });
      setBody('');
      setFiles([]);
      setInsertedMentions([]);
      onPosted?.(activity);
    } catch (err) {
      toast.error(t('Could not post comment'), { description: err?.message });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="pp-comment-composer">
      <MentionTextarea
        value={body}
        onChange={setBody}
        rows={2}
        placeholder={t('Add a comment — type @ to mention a colleague…')}
        onMentionInserted={(user) =>
          setInsertedMentions((prev) => (prev.some((m) => m.id === user.id) ? prev : [...prev, user]))}
      />

      {files.length > 0 && (
        <div className="pp-comment-composer__files">
          {files.map((f) => (
            <span key={f.key} className={`pp-comment-composer__file${f.status === 'uploading' ? ' is-uploading' : ''}`}>
              {f.status === 'uploading'
                ? <Icon.Spinner size={13} className="pp-comment-composer__spin" />
                : (f.contentType?.startsWith('image/') ? <Icon.Image size={13} /> : <Icon.FileText size={13} />)}
              <span className="pp-comment-composer__file-name">{f.fileName}</span>
              <button
                type="button"
                className="pp-comment-composer__file-remove"
                aria-label={t('Remove {name}', { name: f.fileName })}
                onClick={() => removeFile(f.key)}
              >
                <Icon.X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="pp-comment-composer__actions">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFiles}
        />
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Icon.Paperclip size={14} />}
          onClick={pickFiles}
          disabled={posting || files.length >= MAX_FILES}
        >
          {t('Attach')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={posting}
          disabled={!canPost}
          leadingIcon={<Icon.MessageSquare size={14} />}
          onClick={post}
        >
          {t('Comment')}
        </Button>
      </div>
    </div>
  );
}
